import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ballotInitiativesAPI, candidatesAPI } from '../lib/api';
import { ELECTION } from '../lib/election';
import type { CitizenProfile } from './useCitizenData';
import { padDistrictNumber } from '../../utils/districtDisplay';

const ELECTION_YEAR = new Date(ELECTION.general).getFullYear();

export interface BallotMeasure {
  id: string;
  measure_number?: string | null;
  ballot_title?: string | null;
  popular_name?: string | null;
  unofficial_caption?: string | null;
  sos_initiative_number?: string | null;
  initiative_cycle?: string | null;
  description?: string | null;
  measure_type?: string | null;
  jurisdiction_level?: string | null;
  full_text_url?: string | null;
}

export interface BallotCandidate {
  id: string;
  full_name: string;
  party_affiliation?: string | null;
  is_incumbent?: boolean;
}

export interface BallotRace {
  id: string;
  office: string;
  district: string;
  title: string;
  candidates: BallotCandidate[];
}

// On-ballot measures for this election year (statewide + local that the API returns)
export function useBallotMeasures() {
  return useQuery<BallotMeasure[]>({
    queryKey: ['citizen-ballot-measures'],
    queryFn: async () => {
      const { data } = await ballotInitiativesAPI.getAll({
        status: 'on_ballot',
        election_year: ELECTION_YEAR,
        limit: 100,
      });
      return data.data || [];
    },
  });
}

// Candidate races for the citizen's House + Senate districts (when known)
export function useBallotRaces(profile?: CitizenProfile) {
  return useQuery<BallotRace[]>({
    queryKey: ['citizen-ballot-races', profile?.houseDistrict, profile?.senateDistrict],
    queryFn: async () => {
      const races: BallotRace[] = [];
      const lookups: Array<{ chamber: 'House' | 'Senate'; district: string; title: string }> = [];
      if (profile?.houseDistrict) {
        lookups.push({ chamber: 'House', district: profile.houseDistrict, title: `State House · District ${padDistrictNumber(profile.houseDistrict)}` });
      }
      if (profile?.senateDistrict) {
        lookups.push({ chamber: 'Senate', district: profile.senateDistrict, title: `State Senate · District ${padDistrictNumber(profile.senateDistrict)}` });
      }

      await Promise.all(
        lookups.map(async (l) => {
          try {
            const { data } = await candidatesAPI.getByLegislativeDistrict(l.chamber, l.district);
            const candidates = (data.data || []) as BallotCandidate[];
            if (candidates.length > 0) {
              races.push({ id: `${l.chamber}-${l.district}`, office: l.chamber, district: l.district, title: l.title, candidates });
            }
          } catch {
            /* no candidates for this seat; skip silently */
          }
        })
      );

      return races;
    },
    enabled: !!(profile?.houseDistrict || profile?.senateDistrict),
  });
}

// Local-only ballot marks, keyed per item. The prototype's "mark your ballot"
// is a private planning tool — nothing is submitted server-side.
const STORAGE_KEY = 'citizen-ballot-marks';

export function useBallotMarks() {
  const [marks, setMarks] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(marks));
  }, [marks]);

  const setMark = useCallback((itemId: string, value: string) => {
    setMarks((m) => ({ ...m, [itemId]: value }));
  }, []);

  const clearMark = useCallback((itemId: string) => {
    setMarks((m) => {
      const next = { ...m };
      delete next[itemId];
      return next;
    });
  }, []);

  return { marks, setMark, clearMark };
}
